import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  User, Lock, Mail, Phone, Bell, Shield, ChevronRight,
  ArrowLeft, Save, Loader2, CheckCircle, Eye, EyeOff,
  AlertTriangle, Car, FileText, Home, MapPin,
  UserPlus, Settings, Calendar, DollarSign, Info, CreditCard, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';
import { getUserFriendlyError } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num % 1 === 0) {
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date as MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout, getToken } = useAuth();
  
  // Active section - read from URL params or default to 'policy'
  const tabParam = searchParams.get('tab');
  const validTabs = ['policy', 'billing', 'security', 'profile', 'preferences', 'contact'];
  const initialTab = validTabs.includes(tabParam) ? tabParam : 'policy';
  const [activeSection, setActiveSection] = useState(initialTab);
  
  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveSection(tab);
    setSearchParams({ tab });
    window.scrollTo(0, 0);
  };
  
  // Sync with URL params on mount and when params change
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && validTabs.includes(tab) && tab !== activeSection) {
      setActiveSection(tab);
      window.scrollTo(0, 0);
    }
  }, [searchParams]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Settings data from API
  const [settingsData, setSettingsData] = useState(null);
  
  // Profile form
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    policyNumber: ''
  });
  const [editingName, setEditingName] = useState(false);
  
  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  
  // Password setup required state
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  
  // Contact form
  const [contact, setContact] = useState({
    phone: '',
    mailingAddress: { street: '', city: '', state: '', zip_code: '' },
    garagingAddress: { street: '', city: '', state: '', zip_code: '' },
    emergencyContact: { name: '', phone: '', relationship: '' }
  });
  const [garagingPendingReview, setGaragingPendingReview] = useState(false);
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailReminders: true,
    policyUpdates: true,
    marketingEmails: false,
    smsNotifications: false
  });
  
  // Email change state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangePending, setEmailChangePending] = useState(null);
  
  // Billing state
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [newCardData, setNewCardData] = useState({
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvc: '',
    cardholderName: ''
  });

  useEffect(() => {
    fetchSettings();
    fetchEmailChangeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        navigate('/portal/login');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/portal/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        await logout();
        navigate('/portal/login');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to load settings');
      }
      
      const data = await response.json();
      setSettingsData(data);
      
      // Populate forms
      setProfile({
        firstName: data.profile?.first_name || '',
        lastName: data.profile?.last_name || '',
        email: data.profile?.email || '',
        policyNumber: data.profile?.policy_number || ''
      });
      
      setTwoFactorEnabled(data.security?.two_factor_enabled || false);
      setNeedsPasswordSetup(data.security?.needs_password_setup || false);
      
      setContact({
        phone: data.contact?.phone || '',
        mailingAddress: data.contact?.mailing_address || { street: '', city: '', state: '', zip_code: '' },
        garagingAddress: data.contact?.garaging_address || { street: '', city: '', state: '', zip_code: '' },
        emergencyContact: data.contact?.emergency_contact || { name: '', phone: '', relationship: '' }
      });
      
      setNotifications({
        emailReminders: data.notifications?.email_reminders ?? true,
        policyUpdates: data.notifications?.policy_updates ?? true,
        marketingEmails: data.notifications?.marketing_emails ?? false,
        smsNotifications: data.notifications?.sms_notifications ?? false
      });
      
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailChangeStatus = async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/portal/email-change-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_pending_request) {
          setEmailChangePending(data);
        }
      }
    } catch (err) {
      console.error('Error fetching email change status:', err);
    }
  };

  const handleRequestEmailChange = async (e) => {
    e.preventDefault();
    setEmailChangeLoading(true);
    setError('');
    
    if (!newEmail || !newEmail.includes('@')) {
      showErrorMessage('Please enter a valid email address');
      setEmailChangeLoading(false);
      return;
    }
    
    if (newEmail.toLowerCase() === profile.email.toLowerCase()) {
      showErrorMessage('New email cannot be the same as current email');
      setEmailChangeLoading(false);
      return;
    }
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/request-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ new_email: newEmail })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to request email change');
      }
      
      setShowEmailChangeModal(false);
      setNewEmail('');
      setEmailChangePending({
        has_pending_request: true,
        new_email: newEmail,
        old_email_verified: false,
        new_email_verified: false,
        request_id: data.request_id
      });
      showSuccessMessage(data.message);
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setEmailChangeLoading(true);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/cancel-email-change`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to cancel email change');
      }
      
      setEmailChangePending(null);
      showSuccessMessage('Email change request cancelled');
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleResendEmailVerification = async () => {
    setEmailChangeLoading(true);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/resend-email-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend verification emails');
      }
      
      showSuccessMessage(data.message);
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const showSuccessMessage = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const showErrorMessage = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 5000);
  };

  // Profile handlers
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          first_name: profile.firstName,
          last_name: profile.lastName,
          phone: contact.phone
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update profile');
      }
      
      setEditingName(false);
      showSuccessMessage('Profile updated successfully');
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Password handlers
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    // Validate passwords
    if (passwordData.newPassword.length < 8) {
      showErrorMessage('Password must be at least 8 characters');
      setSaving(false);
      return;
    }
    
    if (!/[A-Z]/.test(passwordData.newPassword)) {
      showErrorMessage('Password must contain at least one uppercase letter');
      setSaving(false);
      return;
    }
    
    if (!/[a-z]/.test(passwordData.newPassword)) {
      showErrorMessage('Password must contain at least one lowercase letter');
      setSaving(false);
      return;
    }
    
    if (!/\d/.test(passwordData.newPassword)) {
      showErrorMessage('Password must contain at least one number');
      setSaving(false);
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorMessage('Passwords do not match');
      setSaving(false);
      return;
    }
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          confirm_password: passwordData.confirmPassword
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to change password');
      }
      
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showSuccessMessage('Password changed successfully');
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Password Setup handler (for first-time setup)
  const handleSetupPassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    // Validate passwords
    if (passwordData.newPassword.length < 8) {
      showErrorMessage('Password must be at least 8 characters');
      setSaving(false);
      return;
    }
    
    if (!/[A-Z]/.test(passwordData.newPassword)) {
      showErrorMessage('Password must contain at least one uppercase letter');
      setSaving(false);
      return;
    }
    
    if (!/[a-z]/.test(passwordData.newPassword)) {
      showErrorMessage('Password must contain at least one lowercase letter');
      setSaving(false);
      return;
    }
    
    if (!/\d/.test(passwordData.newPassword)) {
      showErrorMessage('Password must contain at least one number');
      setSaving(false);
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorMessage('Passwords do not match');
      setSaving(false);
      return;
    }
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/setup-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: passwordData.newPassword,
          confirm_password: passwordData.confirmPassword
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to set up password');
      }
      
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setNeedsPasswordSetup(false);
      showSuccessMessage('Password set up successfully! You can now sign in with your email and password.');
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  // 2FA handlers
  const handleToggle2FA = async () => {
    setToggling2FA(true);
    setError('');
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/toggle-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ enable: !twoFactorEnabled })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update 2FA');
      }
      
      const data = await response.json();
      setTwoFactorEnabled(data.two_factor_enabled);
      showSuccessMessage(`Two-factor authentication ${data.two_factor_enabled ? 'enabled' : 'disabled'}`);
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setToggling2FA(false);
    }
  };

  // Contact handlers
  const handleSaveContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/update-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone: contact.phone,
          mailing_address: contact.mailingAddress,
          garaging_address: contact.garagingAddress,
          emergency_contact: contact.emergencyContact
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update contact information');
      }
      
      const data = await response.json();
      if (data.requires_review) {
        setGaragingPendingReview(true);
      }
      showSuccessMessage(data.message);
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Notification handlers
  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/portal/update-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email_reminders: notifications.emailReminders,
          policy_updates: notifications.policyUpdates,
          marketing_emails: notifications.marketingEmails,
          sms_notifications: notifications.smsNotifications
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update notification preferences');
      }
      
      showSuccessMessage('Notification preferences updated');
      
    } catch (err) {
      showErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Billing functions
  const fetchBillingInfo = async () => {
    try {
      setBillingLoading(true);
      const token = getToken();
      const response = await fetch(`${API_URL}/api/billing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingData(data);
      }
    } catch (err) {
      console.error('Failed to fetch billing info:', err);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleAddCard = async () => {
    try {
      setAddingCard(true);
      const token = getToken();
      
      // Get SetupIntent from backend
      const response = await fetch(`${API_URL}/api/billing/add-card`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to initialize card setup');
      }
      
      const data = await response.json();
      
      if (data.test_mode) {
        // In test mode, simulate adding a card
        const saveResponse = await fetch(`${API_URL}/api/billing/save-card`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            setup_intent_id: data.setup_intent_id,
            brand: 'visa',
            last4: newCardData.cardNumber.slice(-4) || '4242',
            exp_month: parseInt(newCardData.expMonth) || 12,
            exp_year: parseInt(newCardData.expYear) || 2028
          })
        });
        
        if (saveResponse.ok) {
          setSuccess('Card added successfully (test mode)');
          setShowAddCardModal(false);
          setNewCardData({ cardNumber: '', expMonth: '', expYear: '', cvc: '', cardholderName: '' });
          fetchBillingInfo();
        }
      } else {
        // For live mode, would integrate with Stripe Elements
        setError('Live card processing requires Stripe Elements integration');
      }
    } catch (err) {
      setError(getUserFriendlyError(err.message) || 'Failed to add card');
    } finally {
      setAddingCard(false);
    }
  };

  const handleSetDefaultCard = async (paymentMethodId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/billing/set-default/${paymentMethodId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSuccess('Default payment method updated');
        fetchBillingInfo();
      }
    } catch (err) {
      setError('Failed to update default card');
    }
  };

  const handleRemoveCard = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this card?')) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/billing/remove-card/${paymentMethodId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSuccess('Card removed successfully');
        fetchBillingInfo();
      }
    } catch (err) {
      setError('Failed to remove card');
    }
  };

  // Fetch billing info when billing section is selected
  useEffect(() => {
    if (activeSection === 'billing' && !billingData) {
      fetchBillingInfo();
    }
  }, [activeSection]);

  const sections = [
    { id: 'policy', label: 'Policy', icon: FileText },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'preferences', label: 'Preferences', icon: Bell },
    { id: 'contact', label: 'Contact', icon: Phone }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#3B5BDB] mx-auto" />
          <p className="mt-4 text-slate-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <PortalLayout
      pageTitle="Settings"
      policyNumber={profile?.policyNumber}
      userName={profile?.firstName}
    >
      <div className="bg-slate-50 min-h-full py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/portal')}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#3B5BDB] font-medium mb-6 transition-colors group"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>

          {/* Page Title */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#E8EEFF] rounded-lg">
                <Settings className="h-5 w-5 text-[#3B5BDB]" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Account Settings
              </h1>
            </div>
            <p className="text-slate-600">
              Manage your profile, security, contact information, and preferences
            </p>
          </div>

          {/* Success/Error Messages */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700 animate-fade-in">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              {success}
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 animate-fade-in">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {/* Password Setup Required Notification */}
          {needsPasswordSetup && (
            <div className="mb-6 p-5 bg-amber-50 border-2 border-amber-300 rounded-2xl shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-100 rounded-xl flex-shrink-0">
                  <Lock className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 text-lg mb-1">
                    Secure Your Account
                  </h3>
                  <p className="text-amber-800 text-sm mb-3">
                    Your account was activated with a verification code. Please set up a password to secure your account and enable easier sign-in.
                  </p>
                  <button
                    onClick={() => handleTabChange('security')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors text-sm"
                    data-testid="setup-password-btn"
                  >
                    <Shield className="h-4 w-4" />
                    Set Up Password Now
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-6">
            {/* Sidebar Navigation */}
            <div className="md:col-span-1">
              <nav className="space-y-1 sticky top-24">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleTabChange(section.id)}
                      data-testid={`nav-${section.id}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        activeSection === section.id
                          ? 'bg-[#3B5BDB] text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Content Area */}
            <div className="md:col-span-3">
              
              {/* ==================== PROFILE SECTION ==================== */}
              {activeSection === 'profile' && (
                <Card className="shadow-lg border-0 rounded-2xl">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Profile Information</h2>
                        <p className="text-sm text-slate-600">Your personal details</p>
                      </div>
                    </div>
                    
                    <form onSubmit={handleSaveProfile} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName" className="text-slate-700 font-medium">First Name</Label>
                          <Input
                            id="firstName"
                            value={profile.firstName}
                            onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                            disabled={!editingName}
                            className={`mt-1.5 ${!editingName ? 'bg-slate-50' : ''}`}
                            data-testid="profile-first-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName" className="text-slate-700 font-medium">Last Name</Label>
                          <Input
                            id="lastName"
                            value={profile.lastName}
                            onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                            disabled={!editingName}
                            className={`mt-1.5 ${!editingName ? 'bg-slate-50' : ''}`}
                            data-testid="profile-last-name"
                          />
                        </div>
                      </div>
                      
                      {!editingName && (
                        <button
                          type="button"
                          onClick={() => setEditingName(true)}
                          className="text-sm text-[#3B5BDB] hover:underline font-medium"
                        >
                          Edit name
                        </button>
                      )}
                      
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          value={profile.email}
                          disabled
                          className="mt-1.5 bg-slate-50"
                          data-testid="profile-email"
                        />
                        
                        {/* Pending Email Change Status */}
                        {emailChangePending && (
                          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <div className="flex items-start gap-3">
                              <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                              <div className="flex-1">
                                <p className="font-medium text-blue-900">Email change pending</p>
                                <p className="text-sm text-blue-700 mt-1">
                                  New email: <span className="font-medium">{emailChangePending.new_email}</span>
                                </p>
                                <div className="flex items-center gap-4 mt-2 text-xs">
                                  <span className={emailChangePending.old_email_verified ? 'text-green-600' : 'text-slate-500'}>
                                    {emailChangePending.old_email_verified ? '✓' : '○'} Current email verified
                                  </span>
                                  <span className={emailChangePending.new_email_verified ? 'text-green-600' : 'text-slate-500'}>
                                    {emailChangePending.new_email_verified ? '✓' : '○'} New email verified
                                  </span>
                                </div>
                                <div className="flex gap-2 mt-3">
                                  <button
                                    type="button"
                                    onClick={handleResendEmailVerification}
                                    disabled={emailChangeLoading}
                                    className="text-xs text-blue-700 hover:text-blue-800 font-medium"
                                  >
                                    {emailChangeLoading ? 'Sending...' : 'Resend verification emails'}
                                  </button>
                                  <span className="text-blue-300">|</span>
                                  <button
                                    type="button"
                                    onClick={handleCancelEmailChange}
                                    disabled={emailChangeLoading}
                                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                                  >
                                    Cancel request
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {!emailChangePending && (
                          <button
                            type="button"
                            onClick={() => setShowEmailChangeModal(true)}
                            className="text-sm text-[#3B5BDB] hover:underline font-medium mt-2"
                            data-testid="change-email-btn"
                          >
                            Change email address
                          </button>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="policyNumber" className="text-slate-700 font-medium">Policy Number</Label>
                        <Input
                          id="policyNumber"
                          value={profile.policyNumber}
                          disabled
                          className="mt-1.5 bg-slate-50 font-mono"
                          data-testid="profile-policy-number"
                        />
                      </div>
                      
                      {editingName && (
                        <div className="pt-4 flex gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setEditingName(false);
                              setProfile({
                                ...profile,
                                firstName: settingsData?.profile?.first_name || '',
                                lastName: settingsData?.profile?.last_name || ''
                              });
                            }}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={saving}
                            className="flex-1 bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                            data-testid="save-profile-btn"
                          >
                            {saving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Changes
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* ==================== SECURITY SECTION ==================== */}
              {activeSection === 'security' && (
                <div className="space-y-6">
                  {/* Password Setup/Change Card */}
                  <Card className="shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className={`p-2 rounded-lg ${needsPasswordSetup ? 'bg-amber-100' : 'bg-amber-100'}`}>
                          <Lock className={`h-5 w-5 ${needsPasswordSetup ? 'text-amber-600' : 'text-amber-600'}`} />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">
                            {needsPasswordSetup ? 'Set Up Password' : 'Change Password'}
                          </h2>
                          <p className="text-sm text-slate-600">
                            {needsPasswordSetup 
                              ? 'Create a password to secure your account' 
                              : 'Update your account password'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Password Setup Required Alert */}
                      {needsPasswordSetup && (
                        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <p className="text-sm text-amber-800">
                            <strong>Action Required:</strong> Your account was activated using a verification code. 
                            Please create a password to enable secure sign-in.
                          </p>
                        </div>
                      )}
                      
                      <form onSubmit={needsPasswordSetup ? handleSetupPassword : handleChangePassword} className="space-y-4">
                        {/* Current Password - Only show for password change, not setup */}
                        {!needsPasswordSetup && (
                          <div>
                            <Label htmlFor="currentPassword" className="text-slate-700 font-medium">Current Password</Label>
                            <div className="relative mt-1.5">
                              <Input
                                id="currentPassword"
                                type={showCurrentPassword ? 'text' : 'password'}
                                value={passwordData.currentPassword}
                                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                className="pr-10"
                                required
                                data-testid="current-password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <Label htmlFor="newPassword" className="text-slate-700 font-medium">New Password</Label>
                          <div className="relative mt-1.5">
                            <Input
                              id="newPassword"
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                              className="pr-10"
                              required
                              data-testid="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1.5">
                            Must be 8+ characters with uppercase, lowercase, and number
                          </p>
                        </div>
                        
                        <div>
                          <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm New Password</Label>
                          <div className="relative mt-1.5">
                            <Input
                              id="confirmPassword"
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                              className="pr-10"
                              required
                              data-testid="confirm-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        
                        <div className="pt-4">
                          <Button
                            type="submit"
                            disabled={saving}
                            className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                            data-testid="change-password-btn"
                          >
                            {saving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {needsPasswordSetup ? 'Setting up...' : 'Updating...'}
                              </>
                            ) : (
                              needsPasswordSetup ? 'Set Password' : 'Update Password'
                            )}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>

                  {/* 2FA Card */}
                  <Card className="shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Shield className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">Two-Factor Authentication</h2>
                          <p className="text-sm text-slate-600">Add an extra layer of security</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${twoFactorEnabled ? 'bg-green-500' : 'bg-slate-300'}`}></div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {twoFactorEnabled ? '2FA is enabled' : '2FA is disabled'}
                            </p>
                            <p className="text-sm text-slate-600">
                              {twoFactorEnabled 
                                ? 'Your account has an extra layer of protection' 
                                : 'Enable 2FA for additional account security'}
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={handleToggle2FA}
                          disabled={toggling2FA}
                          variant={twoFactorEnabled ? 'outline' : 'default'}
                          className={twoFactorEnabled ? '' : 'bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white'}
                          data-testid="toggle-2fa-btn"
                        >
                          {toggling2FA ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : twoFactorEnabled ? (
                            'Disable'
                          ) : (
                            'Enable'
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* ==================== BILLING SECTION ==================== */}
              {activeSection === 'billing' && (
                <div className="space-y-6">
                  {/* Expiring Card Warning */}
                  {billingData?.expiring_cards?.length > 0 && (
                    <Card className="border-2 border-amber-300 bg-amber-50 rounded-2xl">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                          <div>
                            <h3 className="font-semibold text-amber-800">Card Expiring Soon</h3>
                            {billingData.expiring_cards.map((card) => (
                              <p key={card.id} className="text-sm text-amber-700 mt-1">
                                Your {card.brand} ending in {card.last4} expires in {card.days_until_expiry} days 
                                ({card.exp_month}/{card.exp_year}). Please update your payment method.
                              </p>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Payment Methods Card */}
                  <Card className="shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <DollarSign className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-slate-900">Saved Payment Method</h2>
                            <p className="text-sm text-slate-600">Used for renewals and installment payments only</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => setShowAddCardModal(true)}
                          className="bg-[#3B5BDB] hover:bg-[#2f4bc0] text-white"
                        >
                          {billingData?.payment_methods?.length > 0 ? 'Replace Card' : 'Add Card'}
                        </Button>
                      </div>
                      
                      {/* Authorization Scope Notice */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-xs text-blue-800">
                          Your saved card is authorized only for scheduled policy renewals and installment payments. 
                          Any other charges require separate authorization.
                        </p>
                      </div>

                      {billingLoading ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-[#3B5BDB]" />
                        </div>
                      ) : billingData?.payment_methods?.length > 0 ? (
                        <div className="space-y-3">
                          {billingData.payment_methods.map((card) => (
                            <div 
                              key={card.id} 
                              className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                                card.is_default ? 'border-[#3B5BDB] bg-blue-50' : 'border-slate-200'
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-8 bg-slate-100 rounded flex items-center justify-center">
                                  <span className="text-xs font-bold text-slate-600 uppercase">
                                    {card.brand}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    •••• •••• •••• {card.last4}
                                    {card.is_default && (
                                      <span className="ml-2 text-xs bg-[#3B5BDB] text-white px-2 py-0.5 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    Expires {card.exp_month}/{card.exp_year}
                                  </p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    Authorized for scheduled payments only
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!card.is_default && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSetDefaultCard(card.id)}
                                  >
                                    Set Default
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => handleRemoveCard(card.id)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <DollarSign className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No payment method saved</p>
                          <p className="text-sm mt-1">Add a card for renewals and installment payments</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Next Payment Card */}
                  {billingData?.next_payment && (
                    <Card className="shadow-lg border-0 rounded-2xl">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Calendar className="h-5 w-5 text-blue-600" />
                          </div>
                          <h2 className="text-lg font-semibold text-slate-900">Next Payment</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-slate-50 rounded-xl">
                            <p className="text-sm text-slate-500">Amount Due</p>
                            <p className="text-2xl font-bold text-slate-900">
                              {formatCurrency(billingData.next_payment.amount)}
                            </p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-xl">
                            <p className="text-sm text-slate-500">Due Date</p>
                            <p className="text-2xl font-bold text-slate-900">
                              {formatDate(billingData.next_payment.due_date) || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-slate-500 mt-4">
                          Policy: {billingData.next_payment.policy_number} • {billingData.next_payment.frequency} payment
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Billing History Card */}
                  <Card className="shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <FileText className="h-5 w-5 text-purple-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-slate-900">Billing History</h2>
                      </div>
                      
                      {billingData?.billing_history?.length > 0 ? (
                        <div className="space-y-3">
                          {billingData.billing_history.map((item, index) => (
                            <div key={item.id || index} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                              <div>
                                <p className="font-medium text-slate-900">{item.description}</p>
                                <p className="text-sm text-slate-500">{formatDate(item.date)}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-slate-900">{formatCurrency(item.amount)}</p>
                                <p className={`text-sm ${item.status === 'completed' ? 'text-green-600' : 'text-amber-600'}`}>
                                  {item.status === 'completed' ? 'Paid' : item.status}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                          <p>No billing history</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* ==================== CONTACT SECTION ==================== */}
              {activeSection === 'contact' && (
                <Card className="shadow-lg border-0 rounded-2xl">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Phone className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Contact Information</h2>
                        <p className="text-sm text-slate-600">Manage your contact details and addresses</p>
                      </div>
                    </div>
                    
                    <form onSubmit={handleSaveContact} className="space-y-6">
                      {/* Phone */}
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={contact.phone}
                          onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="mt-1.5"
                          data-testid="contact-phone"
                        />
                      </div>
                      
                      {/* Mailing Address */}
                      <div>
                        <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                          <Mail className="h-4 w-4" />
                          Mailing Address
                        </Label>
                        <div className="space-y-3 bg-slate-50 p-4 rounded-xl">
                          <Input
                            placeholder="Street Address"
                            value={contact.mailingAddress.street || ''}
                            onChange={(e) => setContact({
                              ...contact,
                              mailingAddress: { ...contact.mailingAddress, street: e.target.value }
                            })}
                            data-testid="mailing-street"
                          />
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <Input
                              placeholder="City"
                              value={contact.mailingAddress.city || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                mailingAddress: { ...contact.mailingAddress, city: e.target.value }
                              })}
                              data-testid="mailing-city"
                            />
                            <Input
                              placeholder="State"
                              value={contact.mailingAddress.state || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                mailingAddress: { ...contact.mailingAddress, state: e.target.value }
                              })}
                              data-testid="mailing-state"
                            />
                            <Input
                              placeholder="ZIP Code"
                              value={contact.mailingAddress.zip_code || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                mailingAddress: { ...contact.mailingAddress, zip_code: e.target.value }
                              })}
                              className="col-span-2 sm:col-span-1"
                              data-testid="mailing-zip"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Garaging Address */}
                      <div>
                        <Label className="text-slate-700 font-medium flex items-center gap-2 mb-1">
                          <MapPin className="h-4 w-4" />
                          Garaging Address
                        </Label>
                        <p className="text-xs text-amber-600 mb-3 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Changes to garaging address require review
                        </p>
                        {garagingPendingReview && (
                          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                            Your garaging address change is pending review
                          </div>
                        )}
                        <div className="space-y-3 bg-slate-50 p-4 rounded-xl">
                          <Input
                            placeholder="Street Address"
                            value={contact.garagingAddress.street || ''}
                            onChange={(e) => setContact({
                              ...contact,
                              garagingAddress: { ...contact.garagingAddress, street: e.target.value }
                            })}
                            data-testid="garaging-street"
                          />
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <Input
                              placeholder="City"
                              value={contact.garagingAddress.city || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                garagingAddress: { ...contact.garagingAddress, city: e.target.value }
                              })}
                              data-testid="garaging-city"
                            />
                            <Input
                              placeholder="State"
                              value={contact.garagingAddress.state || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                garagingAddress: { ...contact.garagingAddress, state: e.target.value }
                              })}
                              data-testid="garaging-state"
                            />
                            <Input
                              placeholder="ZIP Code"
                              value={contact.garagingAddress.zip_code || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                garagingAddress: { ...contact.garagingAddress, zip_code: e.target.value }
                              })}
                              className="col-span-2 sm:col-span-1"
                              data-testid="garaging-zip"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Emergency Contact */}
                      <div>
                        <Label className="text-slate-700 font-medium flex items-center gap-2 mb-3">
                          <UserPlus className="h-4 w-4" />
                          Emergency Contact
                        </Label>
                        <div className="space-y-3 bg-slate-50 p-4 rounded-xl">
                          <Input
                            placeholder="Contact Name"
                            value={contact.emergencyContact.name || ''}
                            onChange={(e) => setContact({
                              ...contact,
                              emergencyContact: { ...contact.emergencyContact, name: e.target.value }
                            })}
                            data-testid="emergency-name"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              placeholder="Phone Number"
                              type="tel"
                              value={contact.emergencyContact.phone || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                emergencyContact: { ...contact.emergencyContact, phone: e.target.value }
                              })}
                              data-testid="emergency-phone"
                            />
                            <Input
                              placeholder="Relationship"
                              value={contact.emergencyContact.relationship || ''}
                              onChange={(e) => setContact({
                                ...contact,
                                emergencyContact: { ...contact.emergencyContact, relationship: e.target.value }
                              })}
                              data-testid="emergency-relationship"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-4">
                        <Button
                          type="submit"
                          disabled={saving}
                          className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                          data-testid="save-contact-btn"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Contact Info
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* ==================== POLICY SECTION (READ ONLY) ==================== */}
              {activeSection === 'policy' && (
                <div className="space-y-6">
                  {/* Coverage Details */}
                  <Card className="shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Shield className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">Coverage Details</h2>
                          <p className="text-sm text-slate-600">Your policy coverage information</p>
                        </div>
                      </div>
                      
                      <div className="bg-slate-50 rounded-xl p-5 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm text-slate-600">Policy Number</span>
                          <span className="font-mono font-semibold text-slate-900">{settingsData?.policy?.policy_number}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Status</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            ['active', 'issued'].includes(settingsData?.policy?.status?.toLowerCase())
                              ? 'bg-green-500 text-white'
                              : 'bg-slate-500 text-white'
                          }`}>
                            {settingsData?.policy?.status === 'active' || settingsData?.policy?.status === 'issued' 
                              ? 'Active' 
                              : settingsData?.policy?.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <DollarSign className="h-4 w-4" />
                            <span className="text-xs uppercase tracking-wider">Coverage Amount</span>
                          </div>
                          <p className="font-semibold text-slate-900 text-lg">{formatCurrency(settingsData?.policy?.coverage_amount)}</p>
                          <p className="text-sm text-slate-500">{settingsData?.policy?.coverage_level_name}</p>
                        </div>
                        
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <DollarSign className="h-4 w-4" />
                            <span className="text-xs uppercase tracking-wider">Deductible</span>
                          </div>
                          <p className="font-semibold text-slate-900 text-lg">{formatCurrency(settingsData?.policy?.deductible)}</p>
                        </div>
                        
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <Calendar className="h-4 w-4" />
                            <span className="text-xs uppercase tracking-wider">Effective Date</span>
                          </div>
                          <p className="font-semibold text-slate-900">{formatDate(settingsData?.policy?.effective_date)}</p>
                        </div>
                        
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <Calendar className="h-4 w-4" />
                            <span className="text-xs uppercase tracking-wider">Expiration Date</span>
                          </div>
                          <p className="font-semibold text-slate-900">{formatDate(settingsData?.policy?.expiration_date)}</p>
                        </div>
                        
                        <div className="sm:col-span-2 bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <DollarSign className="h-4 w-4" />
                            <span className="text-xs uppercase tracking-wider">Premium Paid</span>
                          </div>
                          <p className="font-semibold text-green-600 text-lg">{formatCurrency(settingsData?.policy?.premium_paid)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Vehicle Details */}
                  <Card className="shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Car className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">Vehicle Details</h2>
                          <p className="text-sm text-slate-600">Your covered vehicle information</p>
                        </div>
                      </div>
                      
                      <div className="bg-slate-50 rounded-xl p-5">
                        <p className="text-xl font-bold text-slate-900 mb-4">
                          {settingsData?.policy?.vehicle?.year} {settingsData?.policy?.vehicle?.make} {settingsData?.policy?.vehicle?.model}{settingsData?.policy?.vehicle?.trim ? ` ${settingsData.policy.vehicle.trim}` : ''}
                        </p>
                        
                        <div className="grid sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">VIN</p>
                            <p className="font-mono font-medium text-slate-700">{settingsData?.policy?.vehicle?.vin || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Body Class</p>
                            <p className="font-medium text-slate-700">{settingsData?.policy?.vehicle?.body_class || 'N/A'}</p>
                          </div>
                          {settingsData?.policy?.vehicle?.estimated_value && (
                            <div className="sm:col-span-2">
                              <p className="text-slate-500">Estimated Value</p>
                              <p className="font-medium text-slate-700">{formatCurrency(settingsData?.policy?.vehicle?.estimated_value)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-xs text-slate-500 mt-4 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Contact support to make changes to vehicle or coverage information
                      </p>
                    </CardContent>
                  </Card>

                  {/* Cancel Policy */}
                  <Card className="shadow-lg border-0 rounded-2xl border-red-100">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900">Cancel Policy</h2>
                          <p className="text-sm text-slate-600">Request to cancel your coverage</p>
                        </div>
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-4">
                        If you need to cancel your policy, you can submit a cancellation request. Please note that cancellation terms and any applicable refunds will be processed according to your policy agreement.
                      </p>
                      
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/cancel/${settingsData?.policy?.policy_number}`)}
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
                        data-testid="cancel-policy-settings-btn"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Request Cancellation
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* ==================== PREFERENCES SECTION ==================== */}
              {activeSection === 'preferences' && (
                <Card className="shadow-lg border-0 rounded-2xl">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <Bell className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900">Notification Preferences</h2>
                        <p className="text-sm text-slate-600">Choose how you want to be notified</p>
                      </div>
                    </div>
                    
                    <form onSubmit={handleSaveNotifications} className="space-y-4">
                      <div className="space-y-3">
                        <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <Mail className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900">Policy Reminders</p>
                              <p className="text-sm text-slate-600">Renewal and expiration reminders</p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={notifications.emailReminders}
                            onChange={(e) => setNotifications({ ...notifications, emailReminders: e.target.checked })}
                            className="h-5 w-5 rounded border-slate-300 text-[#3B5BDB] focus:ring-[#3B5BDB]"
                            data-testid="notify-reminders"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <Shield className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900">Policy Updates</p>
                              <p className="text-sm text-slate-600">Changes to your policy or coverage</p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={notifications.policyUpdates}
                            onChange={(e) => setNotifications({ ...notifications, policyUpdates: e.target.checked })}
                            className="h-5 w-5 rounded border-slate-300 text-[#3B5BDB] focus:ring-[#3B5BDB]"
                            data-testid="notify-updates"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <Phone className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900">SMS Notifications</p>
                              <p className="text-sm text-slate-600">Text message alerts for important updates</p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={notifications.smsNotifications}
                            onChange={(e) => setNotifications({ ...notifications, smsNotifications: e.target.checked })}
                            className="h-5 w-5 rounded border-slate-300 text-[#3B5BDB] focus:ring-[#3B5BDB]"
                            data-testid="notify-sms"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <Bell className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900">Marketing Emails</p>
                              <p className="text-sm text-slate-600">News, tips, and special offers</p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={notifications.marketingEmails}
                            onChange={(e) => setNotifications({ ...notifications, marketingEmails: e.target.checked })}
                            className="h-5 w-5 rounded border-slate-300 text-[#3B5BDB] focus:ring-[#3B5BDB]"
                            data-testid="notify-marketing"
                          />
                        </label>
                      </div>
                      
                      <div className="pt-4">
                        <Button
                          type="submit"
                          disabled={saving}
                          className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                          data-testid="save-notifications-btn"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Preferences
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Change Modal */}
      {showEmailChangeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-fade-in">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Change Email Address
                  </h2>
                  <p className="text-sm text-slate-600">
                    Verification required on both emails
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleRequestEmailChange} className="p-6 space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> For security, we'll send verification emails to both your current ({profile.email}) and new email address. Both must be verified to complete the change.
                </p>
              </div>
              
              <div>
                <Label htmlFor="newEmail" className="text-slate-700 font-medium">
                  New Email Address
                </Label>
                <Input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter your new email address"
                  className="mt-1.5"
                  required
                  data-testid="new-email-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEmailChangeModal(false);
                    setNewEmail('');
                  }}
                  className="flex-1"
                  disabled={emailChangeLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white"
                  disabled={emailChangeLoading || !newEmail}
                  data-testid="submit-email-change-btn"
                >
                  {emailChangeLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Verification'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Add Payment Method</h3>
              <button 
                onClick={() => setShowAddCardModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="cardholderName">Cardholder Name</Label>
                <Input
                  id="cardholderName"
                  placeholder="John Doe"
                  value={newCardData.cardholderName}
                  onChange={(e) => setNewCardData({ ...newCardData, cardholderName: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="cardNumber">Card Number</Label>
                <Input
                  id="cardNumber"
                  placeholder="4242 4242 4242 4242"
                  value={newCardData.cardNumber}
                  onChange={(e) => setNewCardData({ ...newCardData, cardNumber: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="expMonth">Month</Label>
                  <Input
                    id="expMonth"
                    placeholder="MM"
                    maxLength={2}
                    value={newCardData.expMonth}
                    onChange={(e) => setNewCardData({ ...newCardData, expMonth: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                  />
                </div>
                <div>
                  <Label htmlFor="expYear">Year</Label>
                  <Input
                    id="expYear"
                    placeholder="YYYY"
                    maxLength={4}
                    value={newCardData.expYear}
                    onChange={(e) => setNewCardData({ ...newCardData, expYear: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  />
                </div>
                <div>
                  <Label htmlFor="cvc">CVC</Label>
                  <Input
                    id="cvc"
                    placeholder="123"
                    maxLength={4}
                    type="password"
                    value={newCardData.cvc}
                    onChange={(e) => setNewCardData({ ...newCardData, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-blue-700">
                  <Info className="h-4 w-4 inline mr-1" />
                  This is a test environment. No real charges will be made.
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddCardModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#3B5BDB] hover:bg-[#2f4bc0] text-white"
                  onClick={handleAddCard}
                  disabled={addingCard || !newCardData.cardNumber || !newCardData.expMonth || !newCardData.expYear}
                >
                  {addingCard ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Adding...
                    </>
                  ) : (
                    'Add Card'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
